import { markRaw } from 'vue';
// SimplePeer, SignalR bağlantısı, medya akışı ve kaydedici Vue 3 reaktif proxy'sine sarılmaz
// (proxy üzerinden çağrılan yerel/dahili API'ler bozulur).
import {
  HubConnectionBuilder,
  LogLevel
} from "@microsoft/signalr";
import { startConnectionQualityMonitor } from "../helpers/connectionQuality";

// TURN alinamadiginda gorusme hic acilmaktansa STUN'la denensin: bu, TURN
// eklenmeden onceki davranistir, yani en kotu ihtimalde geriye dusulur.
function fallbackIceServers() {
  return [{ urls: 'stun:stun.cloudflare.com:3478' }];
}

// Uzak `<video>` akis kesilince son kareyi tutar: karsi taraf koptugunda ekranda
// donmus bir goruntu kalir ve canli sanilir. Tarayici, RTP'si kesilen alici
// track'ini `muted` isaretler; sahne bu bayrakla durumu gosterir.
// Kamera kapatma yanlis pozitif uretmez: `enabled = false` gonderirken akis
// siyah kare olarak surer, track mute olmaz.
function watchRemoteMedia(stream, onChange) {
  const videoTracks = stream.getVideoTracks();
  const tracks = videoTracks.length > 0 ? videoTracks : stream.getTracks();
  const refresh = () => onChange(tracks.some(track => track.readyState === 'live' && !track.muted));

  tracks.forEach(track => {
    track.addEventListener('mute', refresh);
    track.addEventListener('unmute', refresh);
    track.addEventListener('ended', refresh);
  });

  refresh();
}



const mutations = {
  saveSimplepeer(state, instance) {
    state.simplepeer = instance ? markRaw(instance) : instance;
  },
  saveIceServers(state, iceServers) {
    state.iceServers = iceServers;
  },
  saveMeetingStream(state, stream) {
    state.meetingStream = stream ? markRaw(stream) : stream;
  },
  setIsMeetingStarted(state, isMeetingStarted) {
    state.isMeetingStarted = isMeetingStarted;
  },
  saveMeethub(state, instance) {
    state.meethub = instance ? markRaw(instance) : instance;
  },
  saveMeetingRecorder(state, recorder) {
    state.meetingRecorder = recorder ? markRaw(recorder) : recorder;
  },
}

const actions = {
  initializeMeetingRecorder({
    commit
  }, recorder) {
    commit("saveMeetingRecorder", recorder);
  },
  initializeSimplepeer({
    commit,
    getters,
    state
  }, isHost) {

    // Peer yeniden kuruluyorsa onceki oturumun medya durumu devredilmez.
    state.remoteMediaLive = false;

    let stopQualityMonitor = null;

    const clearQualityMonitor = () => {
      if (stopQualityMonitor) {
        stopQualityMonitor();
        stopQualityMonitor = null;
      }
      state.connectionQuality = null;
    };

    clearQualityMonitor();

    const simplepeer = new window.SimplePeer({
      initiator: isHost == true,
      // Trickle kapaliyken taraflar BUTUN ICE adaylari toplanana kadar tek bir SDP
      // bekliyordu; mobil agda bu 10-30 sn gecikme ya da zaman asimi uretiyordu.
      // Acikken adaylar toplandikca gonderilir ve ilk calisan yol bulunur bulunmaz
      // baglanti kurulur.
      trickle: true,
      reconnectTimer: 500,
      iceTransportPolicy: "all",
      stream: getters.meetingStream,
      offerConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      },
      config: {
        // Liste `fetchIceServers` ile sunucudan gelir: TURN kimlik bilgisi kisa
        // omurludur ve koda gomulmez.
        iceServers: state.iceServers || fallbackIceServers()
      }
    });

    simplepeer.on('error', err => {
      state.simplepeerConnected = false;
      state.remoteMediaLive = false;
      clearQualityMonitor();
      console.log('error', err)
    });

    simplepeer.on('connect', function () {
      state.simplepeerConnected = true;

      let pathLogged = false;

      // `_pc` ozel alandir; paket `RTCPeerConnection`'i baska turlu disari vermiyor
      // ve istatistikler yalniz oradan okunabiliyor.
      stopQualityMonitor = startConnectionQualityMonitor(simplepeer._pc, function (sample) {
        state.connectionQuality = sample;

        // TURN'un gercekten devreye girip girmedigi baska hicbir yerde gorunmuyor;
        // baglanti basina bir kez yazilir.
        if (!pathLogged) {
          pathLogged = true;
          console.info(`WebRTC yolu: ${sample.relay ? 'TURN (relay)' : 'dogrudan'} - RTT ${sample.rttMs} ms`);
        }
      });
    });

    simplepeer.on('signal', function (data) {
      if (data.renegotiate || data.transceiverRequest) return

      // Trickle acikken bu olay her ICE adayi icin ayri tetiklenir. Eski hal her
      // sinyali 500 ms geciktiriyordu; adaylarin tamami o kadar geciktiginde
      // trickle'in kazandirdigi sure geri veriliyordu.
      if (!state.meethub) return;

      const sent = state.meethub.invoke("SendSignal", JSON.stringify(data));

      // Hub o an kopmussa `invoke` reddedilir; yakalanmazsa konsola "Uncaught
      // (in promise)" duser. Kaybedilen aday baglantiyi bozmaz.
      if (sent && typeof sent.catch === 'function') {
        sent.catch(error => console.error(error));
      }
    });

    simplepeer.on('close', function () {
      state.simplepeerConnected = false;
      state.remoteMediaLive = false;
      clearQualityMonitor();
    });

    simplepeer.on('stream', function (stream) {
      // video.js kaldirildi: id artik dogrudan <video> elemanina ait.
      const remotePlayer = document.getElementById('remotePlayer');
      if (remotePlayer) {
        const remoteStream = new MediaStream(stream);
        remotePlayer.srcObject = remoteStream;
        remotePlayer.play();
      }

      watchRemoteMedia(stream, function (isLive) {
        state.remoteMediaLive = isLive;
      });

      if (document.getElementById('localPlayer')) {
        const localPlayer = document.getElementById('localPlayer');

        if (localPlayer) {
          localPlayer.srcObject = new MediaStream(getters.meetingStream);

          localPlayer.play();
        }
      }
    });
    commit("saveSimplepeer", simplepeer);
  },
  setMeetingStream({
    commit
  }, stream) {
    commit("saveMeetingStream", stream);
  },
  setIsMeetingStarted({
    commit
  }, isMeetingStarted) {
    commit("setIsMeetingStarted", isMeetingStarted)
  },
  // Liste servis katmanindan gelir; bos/hatali gelirse STUN'a dusulur.
  // HTTP cagrisi burada yapilmaz: store -> services/index -> store/index
  // dairesel import zinciri olusurdu.
  setIceServers({ commit }, iceServers) {
    commit("saveIceServers",
      Array.isArray(iceServers) && iceServers.length > 0 ? iceServers : fallbackIceServers());
  },
  // Görüşme sayfasından çıkılınca bir sonraki açılış temiz başlar: eski bağlantı,
  // peer ve medya akışı store'da kalırsa yeni sayfa onları devralıyordu.
  resetMeetingRoom({ commit, dispatch }) {
    commit("saveMeethub", null);
    dispatch("resetMeetingMedia");
  },
  // Pencere bekleme ekranına geçince: oda bağlantısı kalır, görüntü bağlantısı ve
  // medya bırakılır.
  resetMeetingMedia({ commit, state }) {
    commit("saveSimplepeer", null);
    commit("saveMeetingStream", null);
    state.simplepeerConnected = false;
    state.remoteMediaLive = false;
    state.connectionQuality = null;
    state.isMeetingStarted = false;
  },
  initializeMeethub({
    commit
  }, url) {
    const connection = new HubConnectionBuilder()
      .withUrl(url)
      .configureLogging(LogLevel.Error)
      // Sunucu istemciyi 60 sn sessizlikten sonra dusuruyor (`AddSignalR` ->
      // `ClientTimeoutInterval`); istemci esigi de ayni olmali, yoksa arka
      // plandaki sekme sunucu hala bagliyken baglantiyi kopariyordu.
      .withServerTimeout(60000)
      .withAutomaticReconnect([0, 3000, 5000, 7000, 10000, 15000])
      .build();

    commit("saveMeethub", connection);
  }
}
const getters = {
  meetingStream: state => {
    return state.meetingStream;
  },
  simplepeerConnected: state => {
    return state.simplepeerConnected;
  },
  remoteMediaLive: state => {
    return state.remoteMediaLive;
  },
  isMeetingStarted: state => {
    return state.isMeetingStarted;
  },
  meethub: state => {
    return state.meethub;
  }
}

export default {
  namespaced: true,
  state: () => {
    return {
      simplepeerConnected: false,
      // Uzak goruntunun canliligi peer'in bagli olmasindan ayridir: peer ayakta
      // kalip medya kesilebilir (bkz. `watchRemoteMedia`).
      remoteMediaLive: false,
      // `{level, rttMs, lossPercent, relay}` - gorusme sirasinda duzenli olculur.
      connectionQuality: null,
      meetingStream: null,
      simplepeer: null,
      iceServers: null,
      isMeetingStarted: false,
      meethub: null,
      meetingRecorder: null
    };
  },
  getters,
  actions,
  mutations
}
